import type {
  BulkThreadActionResponse,
  CreateMessageBody,
  FetchMessagesResponse,
  ListThreadsResponse,
  MessageAcceptanceResponse,
  ThreadResourcesResponse,
  ThreadView,
  WaitingListResponse,
} from '../types/api'
import { api } from './client'

function sendMessage(
  topicId: string,
  body: CreateMessageBody,
): Promise<MessageAcceptanceResponse> {
  return api.post<MessageAcceptanceResponse>(
    `/api/topics/${topicId}/messages`,
    body,
  )
}

export const messagesApi = {
  thread: (
    topicId: string,
    threadId: string,
    opts?: { limit?: number; before?: number },
  ) => {
    const params = new URLSearchParams()
    if (opts?.limit != null) params.set('limit', String(opts.limit))
    if (opts?.before != null) params.set('before', String(opts.before))
    const qs = params.size > 0 ? `?${params}` : ''
    return api.get<FetchMessagesResponse>(
      `/api/topics/${topicId}/threads/${threadId}/messages${qs}`,
    )
  },
  /** List threads in a bucket. `view` defaults to `active` server-side. */
  listThreads: (topicId: string, view: ThreadView = 'active') =>
    api.get<ListThreadsResponse>(`/api/topics/${topicId}/threads?view=${view}`),
  /** Workspace-wide waiting-on-you list (unresolved agent review items,
   * oldest first). Powers the rail badge and the redesigned inbox. */
  waiting: (workspaceId: string) =>
    api.get<WaitingListResponse>(`/api/workspaces/${workspaceId}/waiting`),
  /** Resources used within a thread — the "used in thread" drawer scope. */
  threadResources: (topicId: string, threadId: string) =>
    api.get<ThreadResourcesResponse>(
      `/api/topics/${topicId}/threads/${threadId}/resources`,
    ),
  /** Pin a user-set title; freezes auto-retitling. */
  pinThreadTitle: (topicId: string, threadId: string, title: string) =>
    api.patch<void>(`/api/topics/${topicId}/threads/${threadId}/title`, {
      title,
    }),
  resolveThread: (topicId: string, threadId: string) =>
    api.post<void>(`/api/topics/${topicId}/threads/${threadId}/resolve`, {}),
  reopenThread: (topicId: string, threadId: string) =>
    api.post<void>(`/api/topics/${topicId}/threads/${threadId}/reopen`, {}),

  // ── Bulk archive/trash lifecycle. All take a list of thread ids; the
  // response echoes the ids actually affected. archive/unarchive/trash/
  // restore are reversible (the FE undo toast calls the inverse); purge and
  // emptyTrash are irreversible and must be confirmed in the FE first. ──
  archiveThreads: (topicId: string, threadIds: string[]) =>
    api.post<BulkThreadActionResponse>(
      `/api/topics/${topicId}/threads/archive`,
      { thread_ids: threadIds },
    ),
  unarchiveThreads: (topicId: string, threadIds: string[]) =>
    api.post<BulkThreadActionResponse>(
      `/api/topics/${topicId}/threads/unarchive`,
      { thread_ids: threadIds },
    ),
  trashThreads: (topicId: string, threadIds: string[]) =>
    api.post<BulkThreadActionResponse>(`/api/topics/${topicId}/threads/trash`, {
      thread_ids: threadIds,
    }),
  restoreThreads: (topicId: string, threadIds: string[]) =>
    api.post<BulkThreadActionResponse>(
      `/api/topics/${topicId}/threads/restore`,
      { thread_ids: threadIds },
    ),
  purgeThreads: (topicId: string, threadIds: string[]) =>
    api.post<BulkThreadActionResponse>(`/api/topics/${topicId}/threads/purge`, {
      thread_ids: threadIds,
    }),
  emptyTrash: (topicId: string) =>
    api.post<BulkThreadActionResponse>(
      `/api/topics/${topicId}/threads/empty-trash`,
      {},
    ),

  send: sendMessage,
  cancel: (topicId: string) =>
    api.post<void>(`/api/topics/${topicId}/cancel`, {}),
}
